import * as React from "react";
import {KeyboardEvent} from "react";
import Select from 'react-select';
import {none, Option, option} from "ts-option";
import {FocusableProps} from "../../common/props/FocusableProps";
import {PressableProps} from "../../common/props/PresableProps";
import {PlaceholderProps} from "../../common/props/PlaceholderProps";
import {SearchableProps} from "../../common/props/SearchableProps";
import {ClearableProps} from "../../common/props/ClearableProps";
import {DisableableProps} from "../../common/props/DisableableProps";
import * as classNames from "classnames";
import {isDefined, isPhoneScreen} from "../../../utils/Utils";
import {InputActionMeta} from "react-select/lib/types";

export interface SelectComponentReduxProps<T> {
	/**
	 * All items.
	 */
	items: Option<T[]>;
}

export interface SelectComponentComponentProps<T> {
	/**
	 * Id of a selected element.
	 */
	selectedId: Option<string> | Option<string[]>;
	/**
	 * Callback on changed selection.
	 * @param selected element.
	 */
	onChanged: (selected: Option<T | T[]>) => void;
	/**
	 * On input change event.
	 * @param {string} value Value of the input/
	 */
	onInputChange?: (value: string) => void
	/**
	 * If address selection component should be visible.
	 */
	visible?: boolean
	/**
	 * If multiselect should by able
	 */
	multi?: boolean
	/**
	 * Indicates if items are being loaded
	 */
	isLoading?: boolean
	/**
	 * Render the dropdown menu in document.body via a portal.
	 * Use when the select is inside an overflow:hidden container (e.g. a modal).
	 */
	portalMenu?: boolean
}

interface State {
	/**
	 * Text typed by user.
	 */
	queryText: string;
}

type Props<T> =
	SelectComponentReduxProps<T>
	& SelectComponentComponentProps<T>
	& FocusableProps
	& PressableProps
	& PlaceholderProps
	& SearchableProps
	& ClearableProps
	& DisableableProps

/**
 * Component for selection.
 */
export default abstract class SelectComponent<T, P = {}> extends React.PureComponent<Props<T> & P, State> {

	public static defaultProps: Partial<Props<any>> = {
		visible: true,
		multi: false,
		onInputChange: () => {}
	};

	refs: {
		select: Select<T>;
	};

	constructor(props: Props<T> & P) {
		super(props);
		this.state = {
			queryText: ""
		};
		this.onInputChange = this.onInputChange.bind(this);
		this.findElementById = this.findElementById.bind(this);
		this.renderOption = this.renderOption.bind(this);
		this.renderValue = this.renderValue.bind(this);
		this.onChanged = this.onChanged.bind(this);
	}

	componentDidUpdate(prevProps: Props<T>) {
		if (this.refs.select && prevProps &&
			((prevProps.focus == false && this.props.focus == true) || (!prevProps.visible && this.props.visible)))
			this.refs.select.focus();
	}

	componentDidMount(): void {
		if (this.props.focus && !isPhoneScreen())
			this.refs.select.focus();
	}

	/**
	 * Check if element match to filter query.
	 * @param element element to check.
	 * @param filter filter query.
	 * @return if element match.
	 */
	filter(element: T, filter: string): boolean {
		if (filter.trim().length > 0) {
			return this.matchFilter(element, filter);
		}
		else {
			return true;
		}
	}

	/**
	 * Find selected element.
	 * @return selected element.
	 */
	findSelectedElement(): T | T[] {
		if (!this.props.multi) {
			return ((this.props.selectedId) as Option<string>).map(this.findElementById).orNull;
		}
		else {
			return ((this.props.selectedId) as Option<string[]>).map(id => id.map(this.findElementById)).getOrElse([]);
		}
	}

	/**
	 * Search for element from props items with given identifier.
	 * @param {string} id identifier to look by.
	 * @return {T} found element or null.
	 */
	findElementById(id: string): T {
		const items = this.props.items.getOrElse([]);
		let index = items.map(item => this.elementToId(item)).indexOf(id);
		if (index === -1) {
			return null;
		}
		else {
			return items[index]
		}
	}

	render() {
		let value: T | T[] | SelectComponentDefaultValue = this.findSelectedElement();
		let cfg = {};
		if (!isDefined(value) && this.state.queryText.length) {
			// we need to pass inputValue this way otherwise it breaks some pickers. Too generic component == too painful
			cfg = {
				inputValue: this.state.queryText
			};
			value = new SelectComponentDefaultValue(this.state.queryText, this.state.queryText);
		}

		return (
			<Select
				ref="select"
				className={"c-select-component"}
				classNamePrefix={"c-select-component"}
				isDisabled={this.props.disabled}
				menuPortalTarget={this.props.portalMenu ? document.body : undefined}
				menuPosition={this.props.portalMenu ? "fixed" : undefined}
				styles={{
					container: (base) => ({...base, display: this.props.visible ? '' : 'none'}),
					menuPortal: (base) => ({...base, zIndex: 9999})
				}}
				placeholder={this.props.placeholder}
				filterOption={(option, filter: string) => this.filter(option.data, filter)}
				value={value}
				onFocus={this.props.onFocus}
				isLoading={this.props.items.isEmpty || this.props.isLoading}
				options={this.getDropdownList().getOrElse([])}
				getOptionLabel={data =>
					data instanceof SelectComponentDefaultValue ? this.state.queryText : this.elementToString(data)
				}
				getOptionValue={data =>
					data instanceof SelectComponentDefaultValue ? this.state.queryText : this.elementToString(data)
				}
				onChange={this.onChanged}
				onInputChange={this.onInputChange}
				closeMenuOnSelect={true}
				openMenuOnFocus={true}
				onKeyDown={(evt: KeyboardEvent<HTMLDivElement | HTMLInputElement>) => evt.nativeEvent.key == "Enter" &&
					this.props.onEnter && this.props.onEnter()}
				isSearchable={this.props.searchable}
				isClearable={this.props.clearable}
				isMulti={this.props.multi}
				components={{Option: this.renderOption, SingleValue: this.renderValue}}
				noOptionsMessage={() => "No elements"}
				isOptionDisabled={this.isOptionDisabled}
				{...cfg}/>
		)
	}

	renderOption(props: any) {
		const classFromProps = classNames({
			"c-select-component__option": true,
			"c-select-component__option--is-selected": props.isSelected,
			"c-select-component__option--is-focused": props.isFocused,
			"c-select-component__option--is-disabled": this.isOptionDisabled(props.data),
		});
		return (
			<div ref={props.innerRef} {...props.innerProps} className={classFromProps}>
				<span>
					{props.data instanceof SelectComponentDefaultValue ? this.state.queryText : this.elementToString(props.data)}
				</span>
			</div>
		);
	};

	renderValue(props: any) {
		const classFromProps = classNames({
			"c-select-component__control": true,
			"c-select-component__control--is-focused": props.isFocused,
		});
		return (
			<div ref={props.innerRef} {...props.innerProps} className={classFromProps}>
				<span>
					{props.data instanceof SelectComponentDefaultValue ? this.state.queryText : this.elementToString(props.data)}
				</span>
			</div>
		);
	};

	onInputChange(value: string, inputActionMeta: InputActionMeta) {
		if (inputActionMeta.action === 'input-change') {
			this.setState({queryText: value}, () => {
				this.props.onInputChange(value);
			});
		}
		if (inputActionMeta.action === 'menu-close') {
			this.resetSearchInput();
		}
	}

	onChanged(val?: T | T[]) {
		if (val && val instanceof Array && !val.length) {
			this.props.onChanged(none);
		}
		else {
			this.props.onChanged(option(val));
		}
	}

	/**
	 * Reset search input text.
	 */
	public resetSearchInput() {
		this.setState({queryText: ""});
	}

	getDropdownList(): Option<Array<T>> {
		return this.props.items;
	}

	isOptionDisabled(element: any): boolean {
		return element.disabled;
	}

	/**
	 * Check if element match to filter query.
	 * @param element element to check.
	 * @param filter filter query.
	 * @return if element match.
	 */
	abstract matchFilter(element: T, filter: string): boolean

	/**
	 * Convert element to id.
	 * @param element element to convert.
	 * @return id of element.
	 */
	abstract elementToId(element: T): string

	/**
	 * Conver element to it's display text for query input.
	 * @param {T} element element to convert.
	 * @return {string} display text from element.
	 */
	abstract elementToString(element: T): string
}

export class SelectComponentDefaultValue {
	constructor(public readonly id: string, public readonly label: string) {}
}